import { IItem } from '../../../../api/types/item'
import cx from 'classnames'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  FormControlLabel,
  Switch,
  Theme,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { ItemFormModal } from '../ItemFormModal/ItemFormModal'
import { DeleteIconButton } from '../../../../components/DeleteIconButton/DeleteIconButton'
import { useAuth } from '../../../../context/Auth'
import { EditIconButton } from '../../../../components/EditIconButton/EditIconButton'
import { useInvalidatingMutation } from '../../../../hooks/useInvalidatingMutation'
import { AxiosResponse } from 'axios'
import { mutationDeleteItem } from '../../../../api/mutations'
import { itemsQueryKey } from '../../../../api/queries'
import { Effect } from '../../../../components/Effect/Effect'
import { createStyles, makeStyles } from '@mui/styles'
import { ItemBaseStats } from './components/ItemBaseStats/ItemBaseStats'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    cardReverseBackground: {
      backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#d0d0d0',
    },
    blue: {
      color: theme.palette.mode === 'dark' ? '#3850f1' : '#2139B7FF',
    },
    yellow: {
      color: theme.palette.mode === 'dark' ? '#f1ee38' : '#828f10',
    },
  })
)

interface Props {
  item: IItem
  reverseBackground?: boolean
  additionalActions?: any
  showActions?: boolean
  identified?: boolean
  maxWidth?: number
  elevation?: number
  showTitle?: boolean
}

export const Item: React.FC<Props> = ({
  item,
  reverseBackground = false,
  additionalActions,
  showActions = true,
  identified = true,
  maxWidth = 400,
  elevation = 1,
  showTitle = true,
}) => {
  const auth = useAuth()
  const classes = useStyles()
  const [isIdentified, setIsIdentified] = useState<boolean>(identified)

  const { mutate: handleDeleteItem, isLoading: isMutating } = useInvalidatingMutation<
    AxiosResponse,
    number
  >((id: number) => mutationDeleteItem(id), [itemsQueryKey()])

  return (
    <>
      <Card
        elevation={elevation}
        className={cx(classes.card, reverseBackground && classes.cardReverseBackground)}
      >
        <Box sx={{ maxWidth: `${maxWidth}px` }}>
          {isIdentified && (
            <CardContent>
              {showTitle && (
                <Typography
                  className={cx(
                    item.quality === 'magic' && classes.blue,
                    ['magic', 'common'].includes(item.quality) || classes.yellow
                  )}
                  gutterBottom
                  variant="h5"
                  component="div"
                >
                  {item.name}
                </Typography>
              )}

              <ItemBaseStats item={item} />

              {item.description && (
                <Typography color="text.secondary" gutterBottom align={'justify'}>
                  {item.description}
                </Typography>
              )}
              {item?.effect && <Effect effect={item.effect} />}
            </CardContent>
          )}

          {isIdentified || (
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                {item.name_unidentified}
                <Typography gutterBottom variant="caption" component="div">
                  Niezidentyfikowany
                </Typography>
              </Typography>

              <ItemBaseStats item={item} />

              <Typography color="text.secondary" gutterBottom align={'justify'}>
                {item.description_unidentified}
              </Typography>
            </CardContent>
          )}
        </Box>
        <Box>
          <CardActions>
            <Box>
              {showActions && (auth?.gm || auth?.admin) && (
                <>
                  <DeleteIconButton
                    confirmationBody={
                      <div>
                        Delete item <span className={'font-weight-bold'}>{item.name}</span>?
                      </div>
                    }
                    isLoading={isMutating}
                    onClick={() => handleDeleteItem(item.id)}
                  />
                  <ItemFormModal item={item} title={'Edytuj przedmiot'}>
                    {showModal => <EditIconButton onClick={showModal} />}
                  </ItemFormModal>
                </>
              )}

              {additionalActions && additionalActions(item)}
            </Box>

            {showActions && (auth?.gm || auth?.admin) && item.quality != 'common' && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isIdentified}
                      onChange={e => setIsIdentified(prevState => e.target.checked)}
                    />
                  }
                  label="Zidentyfikowany"
                />
              </Box>
            )}
          </CardActions>
        </Box>
      </Card>
    </>
  )
}
